import React, { Fragment, useEffect, useState } from "react";
import { Button, Table, Modal, Form, FormLabel } from "react-bootstrap";
import { Breadcrumbs, H5, P, ToolTip } from "../../AbstractElements";
import { useTranslation } from "react-i18next";
import { Card, CardHeader, Col, Input, Label } from "reactstrap";
import { Filter, PlusCircle, X } from "react-feather";
import MyPagination from "../pagination";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useLoader } from "../../_helper/LoaderContext";
import { deleteItem, downloadSampleFile, exportMedicineToExcel, getItemsList, uploadBulkItem } from "./PharmacyMedicineService";
import Swal from "sweetalert2";
import { FaDownload, FaPlusCircle, FaUpload } from "react-icons/fa";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import { useAtom } from "jotai";
import { MedicinePageAtom, MedicinePageSizeAtom } from "./MedicineAtoms";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useCurrency } from "../../_helper/CurrencyContext";

const ItemsList = () => {
	const { t } = useTranslation();
	const { getCurrencySymbol } = useCurrency();

	const [searchValue, setSearchValue] = useState('');
	const [totalItems, setTotalItems] = useState(0);
	const [page, setPage] = useAtom(MedicinePageAtom);
	const [size, setSize] = useAtom(MedicinePageSizeAtom);
	const [isClearing, setIsClearing] = useState(false);
	const history = useNavigate();
	const { setLoading } = useLoader();
	const [skeletonLoading, setSkeletonLoading] = useState(false);
	const { pharmacyPOS } = usePharmacy();
	const [itemType, setItemType] = useState("medicine"); // "medicine" or "otc"

	const [editTooltipOpen, setEditTooltipOpen] = useState(false);
	const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
	const [clearTooltipOpen, setClearTooltipOpen] = useState(false);
	const [viewTooltipOpen, setViewTooltipOpen] = useState(false);
	const [deleteTooltipOpen, setDeleteTooltipOpen] = useState(false);
	const toggleEditTooltip = () => setEditTooltipOpen(!editTooltipOpen);
	const toggleFilterTooltip = () => setFilterTooltipOpen(!filterTooltipOpen);
	const toggleClearTooltip = () => setClearTooltipOpen(!clearTooltipOpen);
	const toggleViewTooltip = () => setViewTooltipOpen(!viewTooltipOpen);
	const toggleDeleteTooltip = () => setDeleteTooltipOpen(!deleteTooltipOpen);

	const [brandTooltipOpen, setBrandTooltipOpen] = useState(false);
	const [saltTooltipOpen, setSaltTooltipOpen] = useState(false);
	const [manuTooltipOpen, setManuTooltipOpen] = useState(false);
	const toggleBrandTooltip = () => setBrandTooltipOpen(!brandTooltipOpen);
	const toggleSaltTooltip = () => setSaltTooltipOpen(!saltTooltipOpen);
	const toggleManuTooltip = () => setManuTooltipOpen(!manuTooltipOpen);

	const [formSubmitted, setFormSubmitted] = useState(false);

	const [items, setItems] = useState([]);

	useEffect(() => {
		getItems();
	}, [page, size, itemType]);

	const getItems = async () => {
		const body = {
			clinicId: pharmacyPOS?.hospital_id,
			page: page,
			limit: size,
			search: searchValue.trim(),
			isOTC: itemType === "otc" // Send true for OTC, false for Medicine
		};
		try {
			setLoading(true);
			setSkeletonLoading(true);
			const response = await getItemsList(body);
			if (response.data.status === 'success') {
				setItems(response.data.data.items || []);
				const totalCount = response.data.data.total || 0;
				setTotalItems(Math.ceil(totalCount / size));
			} else {
				setItems([])
				setTotalItems(0)
			}
		} catch (error) {
			console.error("Error fetching items:", error);
		} finally {
			setLoading(false);
			setSkeletonLoading(false);
		}
	};

	const [showImportModal, setShowImportModal] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);

	const handleFileChange = (e) => {
		const file = e.target.files[0];
		setSelectedFile(file);
	};

	const handleUpload = async () => {
		try {
			if (!selectedFile) {
				toast.warning("Please select a file before uploading.");
				return;
			}

			const allowedExtensions = [".csv", ".xlsx"];
			const fileName = selectedFile.name.toLowerCase();
			const isValidExtension = allowedExtensions.some((ext) =>
				fileName.endsWith(ext)
			);

			if (!isValidExtension) {
				toast.warning("Only CSV or XLSX files are allowed.");
				return;
			}
			setLoading(true);
			const formData = new FormData();
			formData.append("clinicId", pharmacyPOS?.clinicId)
			formData.append("file", selectedFile);

			const response = await uploadBulkItem(formData);

			if (response.data?.status === "success") {
				toast.success(response.data.message);
				setSelectedFile(null);
				getItems();
				setShowImportModal(false);
			} else {
				toast.warning(response.data?.message || "File upload failed.");
			}
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleSearchButtonClick = () => {
		if (searchValue.trim !== "") {
			setPage(1);
			getItems();
		}
	};

	const handleClear = () => {
		setIsClearing(true);
		setSearchValue('');
	};

	useEffect(() => {
		if (isClearing) {
			getItems();
			setIsClearing(false);
		}
	}, [isClearing]);

	const handleSearchChange = (event) => {
		setSearchValue(event.target.value);
	};

	const handlePageChange = (pageNumber) => {
		setPage(pageNumber);
	};

	const handlePageSizeChange = (e) => {
		const newSize = parseFloat(e.target.value);
		setSize(newSize);
		setPage(1);
	};

	// Event handler for opening add component
	const addItem = () => {
		history(`${process.env.PUBLIC_URL}/add-item`);
		// if (itemType === "medicine") {
		// } else {
		// 	history(`${process.env.PUBLIC_URL}/add-OTC`);
		// }
	};

	// Event handler for opening edit component
	const handleEditClick = (getID) => {
		if (itemType === "medicine") {
			history(`${process.env.PUBLIC_URL}/update-medicine/${getID}`);
		} else {
			history(`${process.env.PUBLIC_URL}/update-OTC/${getID}`);
		}
	};

	// Event handler for opening view component
	const handleViewClick = (getID) => {
		if (itemType === "medicine") {
			history(`${process.env.PUBLIC_URL}/view-medicine/${getID}`);
		} else {
			history(`${process.env.PUBLIC_URL}/view-OTC/${getID}`);
		}
	};

	const getSampleFile = async () => {
		try {
			setLoading(true);
			const response = await downloadSampleFile();
			if (response.data?.response === "success") {
				const filePath = response.data.data.filePath;
				const fileUrl = `${process.env.REACT_APP_BASE_URL}${filePath}`;
				const link = document.createElement("a");
				link.href = fileUrl;
				link.setAttribute("download", "sample_items.xlsx");
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			} else {
				toast.warning("Failed to generate file.");
			}
		} catch (error) {
			console.error(error);
			toast.warning("Failed to download sample file.");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = async (id) => {
		const confirm = await Swal.fire({
			title: t("Are you sure?"),
			text: t("You want to delete this item"),
			icon: "warning",
			showCancelButton: true,
			customClass: {
				confirmButton: 'btn-primary',
				cancelButton: 'btn-danger',
			},
			confirmButtonText: t("Yes, Delete It!"),
		});

		if (confirm.isConfirmed) {
			try {
				setLoading(true);
				const response = await deleteItem(id);
				if (response?.data?.status === "success") {
					toast.success(response.data.message);
					getItems();
				}
				setLoading(false);
			} catch (err) {
				setLoading(false);
				console.error("Delete error:", err);
			}
		}
	};

	const exportToExcel = () => {
		try {
			setLoading(true);
			// Create worksheet from JSON
			const worksheet = XLSX.utils.json_to_sheet(
				items.map(item => ({
					[itemType === "medicine" ? "Brand Name" : "Product Name"]: itemType === "medicine" ? item.saltName : item.brandName,
					[itemType === "medicine" ? "Salt Name / Composition" : "Product Name"]: itemType === "medicine" ? item.brandName : item.brandName,
					"Manufacturer": item.manufacturer,
					"Product Form": item.productForm,
					"Package": item.package,
					"Quantity": item.packageQuantity,
					"Price": `${getCurrencySymbol()}${item.regularPrice}`,
				}))
			);

			// Auto adjust column widths
			const colWidths = Object.keys(items[0] || {}).map((key) => ({
				wch: Math.max(
					key.length,
					...items.map((item) => String(item[key] || "").length)
				),
			}));
			worksheet["!cols"] = colWidths;

			// Create workbook
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, itemType === "medicine" ? "Medicines" : "OTC Products");

			// Export file
			const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
			const data = new Blob([excelBuffer], { type: "application/octet-stream" });
			saveAs(data, `${itemType === "medicine" ? "medicines" : "otc_products"}.xlsx`);

		} catch (error) {
			console.error("Excel export failed:", error);
			toast.warning("Failed to export items.");
		} finally {
			setLoading(false);
		}
	};

	// Helper function to get table headers based on item type
	const getTableHeaders = () => {
		if (itemType === "medicine") {
			return (
				<tr>
					<th>{t('Brand Name')}</th>
					<th>{t('Salt Name / Composition')}</th>
					<th>{t('Manufacturer Name')}</th>
					<th>{t('Product Form')}</th>
					<th>{t("Packaging Type")}</th>
					<th>{t('Product Quantity')}</th>
					<th>{t('Price')}</th>
					<th className="text-center">{t("Action")}</th>
				</tr>
			);
		} else {
			return (
				<tr>
					<th>{t('Product Name')}</th>
					<th>{t('Manufacturer Name')}</th>
					<th>{t('Product Form')}</th>
					<th>{t("Packaging Type")}</th>
					<th>{t('Quantity')}</th>
					<th>{t('Price')}</th>
					<th className="text-center">{t("Action")}</th>
				</tr>
			);
		}
	};

	// Helper function to get table rows based on item type
	const getTableRows = () => {
		return items.map((item, index) => (
			<tr key={index}>
				{/* Brand/Product Name Column */}
				{itemType === "medicine" ? (
					<>
						{/* Brand Name */}
						<td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							<span id={`brandToolTip-${index}`}>{item.brandName}</span>
							<ToolTip
								attrToolTip={{
									placement: 'top',
									isOpen: brandTooltipOpen === index,
									target: `brandToolTip-${index}`,
									toggle: () =>
										setBrandTooltipOpen(brandTooltipOpen === index ? null : index),
								}}
							>
								<strong>{item.brandName}</strong>
							</ToolTip>
						</td>
						{/* Salt Name / Composition */}
						<td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
							<span id={`saltToolTip-${index}`}>{item.saltName}</span>
							<ToolTip
								attrToolTip={{
									placement: 'top',
									isOpen: saltTooltipOpen === index,
									target: `saltToolTip-${index}`,
									toggle: () =>
										setSaltTooltipOpen(saltTooltipOpen === index ? null : index),
								}}
							>
								<strong>{item.saltName}</strong>
							</ToolTip>
						</td>

					</>
				) : (
					/* Product Name for OTC */
					<td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
						<span id={`brandToolTip-${index}`}>{item.brandName}</span>
						<ToolTip
							attrToolTip={{
								placement: 'top',
								isOpen: brandTooltipOpen === index,
								target: `brandToolTip-${index}`,
								toggle: () =>
									setBrandTooltipOpen(brandTooltipOpen === index ? null : index),
							}}
						>
							<strong>{item.brandName}</strong>
						</ToolTip>
					</td>
				)}

				{/* Manufacturer */}
				<td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
					<span id={`manuToolTip-${index}`}>{item.manufacturer}</span>
					<ToolTip
						attrToolTip={{
							placement: 'top',
							isOpen: manuTooltipOpen === index,
							target: `manuToolTip-${index}`,
							toggle: () =>
								setManuTooltipOpen(manuTooltipOpen === index ? null : index),
						}}
					>
						<strong>{item.manufacturer}</strong>
					</ToolTip>
				</td>

				<td>{item.productForm}</td>
				<td>{item.package}</td>
				<td>{item.packageQuantity}</td>
				<td>{getCurrencySymbol()}{item.regularPrice}</td>
				<td className='text-center'>
					<button className='success-action-button me-1' id="ViewToolTip" onClick={() => handleViewClick(item?._id)}>
						<i className="fa fa-eye" style={{ fontSize: "18px" }}></i>
						<ToolTip
							attrToolTip={{
								placement: 'top',
								isOpen: viewTooltipOpen,
								target: 'ViewToolTip',
								toggle: toggleViewTooltip,
							}} >
							<strong>{t("View")}</strong>
						</ToolTip>
					</button>
					<button className='success-action-button me-1' id="EditToolTip" onClick={() => handleEditClick(item?._id)}>
						<i className="fa fa-pencil-square" style={{ fontSize: "18px" }}></i>
						<ToolTip
							attrToolTip={{
								placement: 'top',
								isOpen: editTooltipOpen,
								target: 'EditToolTip',
								toggle: toggleEditTooltip,
							}} >
							<strong>{t("Edit")}</strong>
						</ToolTip>
					</button>
					{/* <button className='danger-action-button' id="DeleteToolTip" onClick={() => handleDelete(item?._id)}>
						<i className="fa fa-trash" style={{ fontSize: "18px" }}></i>
						<ToolTip
							attrToolTip={{
								placement: 'top',
								isOpen: deleteTooltipOpen,
								target: 'DeleteToolTip',
								toggle: toggleDeleteTooltip,
							}} >
							<strong>{t("Delete")}</strong>
						</ToolTip>
					</button> */}
				</td>
			</tr>
		));
	};

	// Helper to get the placeholder text based on item type
	const getSearchPlaceholder = () => {
		if (itemType === "medicine") {
			return t("Search by brand, salt name and product form");
		} else {
			return t("Search by product name and product form");
		}
	};

	// Helper to get the "No items found" message
	const getNoItemsMessage = () => {
		if (itemType === "medicine") {
			return t('No Medicine found');
		} else {
			return t('No OTC products found');
		}
	};

	// Helper to get the title
	const getTitle = () => {
		if (itemType === "medicine") {
			return t('Medicine');
		} else {
			return t('OTC');
		}
	};

	// Helper to get the add button text
	const getAddButtonText = () => {
		if (itemType === "medicine") {
			return t("Add Items");
		} else {
			return t("Add OTC Item");
		}
	};

	return (
		<Fragment>
			<Breadcrumbs mainTitle={getTitle()} parent={t('Dashboard')} title={getTitle()} />
			<Col sm="12">
				<Card>
					<CardHeader className="d-flex justify-content-end align-items-center">
						<div className="d-flex align-items-center">
							<button
								className="btn btn-primary d-flex align-items-center me-2"
								to=""
								onClick={addItem}>
								<FaPlusCircle className="me-2" /> {t("Add Item")}
							</button>
							<button
								className="btn btn-primary d-flex align-items-center"
								to=""
								onClick={exportToExcel}>
								<FaUpload className="me-2" /> {t("Export to Excel")}
							</button>
						</div>
					</CardHeader>
					<div className="row" style={{ alignItems: "center", padding: "12px 24px" }}>
						<Col sm="12" lg="6" xl="6" md="6" className="align-self-end">
							<Input
								name="SearchData"
								id="SearchData"
								type="text"
								placeholder={getSearchPlaceholder()}
								value={searchValue}
								onChange={handleSearchChange}
								style={{
									lineHeight: 1.8, fontSize: "1rem !important"
								}}
								onKeyDown={(e) => {
									if (e.key === 'Enter') {
										handleSearchButtonClick();
									}
								}}
							/>
						</Col>
						<Col sm="12" lg="4" xl="4" md="4">
							<label className="form-label">{t("Item Type")}</label>
							<div className="mt-2 d-flex align-items-center" style={{ gap: "20px" }}>
								<div className="radio radio-primary">
									<Input
										id="itemMedicine"
										type="radio"
										name="itemType"
										value="medicine"
										checked={itemType === "medicine"}
										onChange={() => {
											setItemType("medicine");
											setPage(1); // Reset to first page when switching
										}}
									/>
									<Label for="itemMedicine" className='ps-3'>
										{t("Medicine")}
									</Label>
								</div>
								<div className="radio radio-primary">
									<Input
										id="itemOtc"
										type="radio"
										name="itemType"
										value="otc"
										checked={itemType === "otc"}
										onChange={() => {
											setItemType("otc");
											setPage(1); // Reset to first page when switching
										}}
									/>
									<Label for="itemOtc" className='ps-3'>
										{t("OTC")}
									</Label>
								</div>
							</div>
						</Col>
						<Col className="col-xl-2 col-lg-4 col-md-4 col-sm-4 d-flex justify-content-start align-items-center" style={{ gap: '18px' }}>
							<button id='FilterToolTip' className='btn btn-primary' style={{ height: "40px" }} onClick={handleSearchButtonClick}>
								<Filter />
							</button>
							<ToolTip
								attrToolTip={{
									placement: 'top',
									isOpen: filterTooltipOpen,
									target: 'FilterToolTip',
									toggle: toggleFilterTooltip,
								}} >
								<strong>{t("Filter")}</strong>
							</ToolTip>
							<button id='ClearToolTip' className='btn btn-secondary' style={{ height: "40px" }} onClick={handleClear}>
								<X />
							</button>
							<ToolTip
								attrToolTip={{
									placement: 'top',
									isOpen: clearTooltipOpen,
									target: 'ClearToolTip',
									toggle: toggleClearTooltip,
								}} >
								<strong>{t("Clear")}</strong>
							</ToolTip>
						</Col>
					</div>
					<div className="card-block">
						<div className='row' style={{ padding: "12px 24px" }}>
							<Col sm="12" lg="12" xl="12">
								<div className="rounded table-responsive">
									<Table className='mb-0 table-center table-bordered table-hover'>
										<thead>
											{getTableHeaders()}
										</thead>
										<tbody>
											{items.length > 0 ? getTableRows() : (
												<tr>
													<td colSpan={itemType === "medicine" ? "8" : "7"} className="text-center text-muted">
														{getNoItemsMessage()}
													</td>
												</tr>
											)}
										</tbody>
									</Table>
								</div>
							</Col>
							<Col sm="12" lg="12" xl="12" className="mt-4">
								<div className="text-center d-md-flex align-items-center justify-content-between">
									<div className="pt-3 show-entries">
										<FormLabel>{t("Show")}&nbsp;</FormLabel>
										<select style={{ display: 'unset', width: 'auto', padding: '0.375rem 2.25rem 0.375rem 0.75rem' }} value={size} onChange={handlePageSizeChange} className="form-control">
											<option value={5}>5</option>
											<option value={10}>10</option>
											<option value={20}>20</option>
											<option value={50}>50</option>
											<option value={75}>75</option>
											<option value={100}>100</option>
										</select>&nbsp;
										<FormLabel>{t("entries")}</FormLabel>
									</div>
									<MyPagination totalPages={totalItems} currentPage={page} onPageChange={handlePageChange} />
								</div>
							</Col>
						</div>
					</div>
				</Card>
			</Col>

			{/* Import Modal - Optional, can be kept for bulk upload */}
			<Modal show={showImportModal} onHide={() => setShowImportModal(false)} centered>
				<Modal.Header closeButton>
					<Modal.Title>{t("Upload Items CSV")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="p-3 mb-3 text-white rounded bg-info">
						<strong>{t("Instructions")}:</strong>
						<ul className="mb-0">
							<li>{t("Download the sample CSV file.")}</li>
							<li>{t("Fill in your item details in the CSV.")}</li>
							<li>{t("Upload the completed CSV file here.")}</li>
						</ul>
					</div>

					<button className="mb-3 btn btn-warning" onClick={getSampleFile}>
						{t("Download Sample")}
					</button>

					<Form.Group controlId="formFile">
						<Form.Label>{t("Select CSV File")}</Form.Label>
						<Form.Control
							type="file"
							accept=".csv, .xlsx"
							onChange={handleFileChange}
							isInvalid={!selectedFile && formSubmitted}
						/>
						<Form.Control.Feedback type="invalid">
							{t("Please select a CSV/XLSX file before uploading.")}
						</Form.Control.Feedback>
					</Form.Group>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="primary"
						onClick={() => {
							setFormSubmitted(true);
							handleUpload();
						}}
					>
						{t("Save")}
					</Button>
					<Button variant="secondary" onClick={() => setShowImportModal(false)}>
						{t("Close")}
					</Button>
				</Modal.Footer>
			</Modal>
		</Fragment>
	);
};

export default ItemsList;